import { useId } from 'react'
import type { BlasonConfigDto } from '@/api/hooks'
import { BLASON_FIELDS, BLASON_GLYPHS, BLASON_PALETTES, BLASON_SHAPES } from './registry'

/**
 * Blason du capitaine. `variant="marker"` = rendu simplifié pour les 16 px du
 * badge de marqueur carte : pas de chef, pas de motif de champ, contour épaissi.
 */
export function CaptainBlason({ config, size = 96, variant = 'full', className = '' }: {
  config: BlasonConfigDto | null
  size?: number
  variant?: 'full' | 'marker'
  className?: string
}) {
  const uid = useId().replace(/:/g, 'b')

  if (!config) return null

  const shape = BLASON_SHAPES[config.shape]
  const palette = BLASON_PALETTES[config.palette]
  const glyph = BLASON_GLYPHS[config.glyph]
  if (!shape || !palette || !glyph) return null

  const field = config.field ? BLASON_FIELDS[config.field] : null
  const isMarker = variant === 'marker'

  /* Ombre portée de l'emblème : même renderer, palette recolorée sombre
     (les glyphs peignent via p.glyph / p.accent), dupliqué translaté. */
  const shadowPalette = { ...palette, glyph: '#05242F', accent: '#05242F' }

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" className={className} role="img" aria-label="Blason du capitaine">
      <defs>
        <linearGradient id={`${uid}-field`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.field[0]} />
          <stop offset="1" stopColor={palette.field[1]} />
        </linearGradient>
        <linearGradient id={`${uid}-chief`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.chief[0]} />
          <stop offset="1" stopColor={palette.chief[1]} />
        </linearGradient>
        <clipPath id={`${uid}-clip`}>
          <path d={shape.outline} />
        </clipPath>
      </defs>
      <path d={shape.outline} fill={`url(#${uid}-field)`} />
      <g clipPath={`url(#${uid}-clip)`}>
        {!isMarker && field ? field(palette) : null}
        {!isMarker && <path d={shape.chief} fill={`url(#${uid}-chief)`} />}
        {/* reflets diagonaux du chef métal (tous les chefs vivent en y 16-40) */}
        {!isMarker && (
          <g fill="#FBF0D2">
            <path d="M 27 16 L 45 16 L 34 40 L 23 40 Z" opacity="0.35" />
            <path d="M 54 16 L 62 16 L 49.5 40 L 41.5 40 Z" opacity="0.28" />
          </g>
        )}
        <g transform="translate(1.5,1.9)" opacity="0.35">{glyph(shadowPalette)}</g>
        {glyph(palette)}
        {/* vignette interne : re-stroke large et sombre de l'outline, clippé */}
        {!isMarker && <path d={shape.outline} fill="none" stroke="#04222C" strokeWidth="10" opacity="0.18" />}
      </g>
      {/* double bordure : liseré sombre sous la bordure accent */}
      <path d={shape.outline} fill="none" stroke="#04222C" strokeWidth={isMarker ? 7 : 5.5} strokeLinejoin="round" opacity="0.5" />
      <path d={shape.outline} fill="none" stroke={palette.accent} strokeWidth={isMarker ? 6 : 3} strokeLinejoin="round" />
    </svg>
  )
}
